// ================================================================================
// ECOMSIMPLY - PRODUCT DETAIL DRAWER COMPONENT
// Drawer détaillé pour afficher les informations complètes d'un produit
// ================================================================================

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ExternalLink, 
  TrendingUp, 
  DollarSign, 
  Star,
  Package,
  AlertCircle,
  CheckCircle,
  Calendar,
  BarChart3,
  Target,
  Lightbulb
} from 'lucide-react';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';

const ProductDetailDrawer = ({ 
  product, 
  isOpen, 
  onClose,
  onViewSuppliers 
}) => {
  
  if (!product) return null;

  const getBankabilityColor = (score) => {
    if (score >= 80) return 'bg-green-500 text-white';
    if (score >= 60) return 'bg-yellow-500 text-white';
    if (score >= 40) return 'bg-orange-500 text-white';
    return 'bg-red-500 text-white';
  };

  const getBankabilityDetails = (score) => {
    if (score >= 80) return {
      label: '🟢 EXCELLENT',
      description: 'Produit très promettteur avec faible risque',
      recommendation: 'Fortement recommandé pour investissement'
    };
    if (score >= 60) return {
      label: '🟡 BON',
      description: 'Produit intéressant avec risque modéré',
      recommendation: 'Recommandé avec analyse approfondie'
    };
    if (score >= 40) return {
      label: '🟠 MOYEN',
      description: 'Produit correct mais risque élevé',
      recommendation: 'Nécessite validation manuelle'
    };
    return {
      label: '🔴 RISQUÉ',
      description: 'Produit à haut risque',
      recommendation: 'Non recommandé sans expertise'
    };
  };

  const bankabilityDetails = getBankabilityDetails(product.bankability_score || 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black bg-opacity-50"
          />
          
          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Analyse Détaillée du Produit
                </h2>
                <Button
                  onClick={onClose}
                  variant="ghost"
                  size="sm"
                  className="p-2"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Image et infos principales */}
              <Card className="p-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <img
                      src={product.image_url || '/api/placeholder/120/120'}
                      alt={product.title}
                      className="w-24 h-24 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/120/120';
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-2">
                      {product.title || 'Titre non disponible'}
                    </h3>
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>ASIN: {product.asin || 'N/A'}</div>
                      <div>Catégorie: {product.category || 'N/A'}</div>
                      <div>Marque: {product.brand || 'N/A'}</div>
                    </div>
                    {product.amazon_url && (
                      <a
                        href={product.amazon_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-primary hover:text-primary/80 text-sm font-medium mt-2"
                      >
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Voir sur Amazon
                      </a>
                    )}
                  </div>
                </div>
              </Card>

              {/* Score de Bankabilité */}
              <Card className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-5 h-5 text-primary" />
                  <h4 className="font-medium text-gray-900">Score de Bankabilité IA</h4>
                </div>
                
                <div className="flex items-center justify-between mb-4">
                  <div className={`inline-flex items-center px-4 py-2 rounded-full text-lg font-bold ${getBankabilityColor(product.bankability_score || 0)}`}>
                    {product.bankability_score || 0}/100
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900">{bankabilityDetails.label}</div>
                    <div className="text-sm text-gray-500">{bankabilityDetails.description}</div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-blue-900">Recommandation IA</div>
                      <div className="text-sm text-blue-700">{bankabilityDetails.recommendation}</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Métriques de Performance */}
              <Card className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  <h4 className="font-medium text-gray-900">Métriques de Performance</h4>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-gray-900">
                      {product.current_price ? `${product.current_price}€` : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">Prix actuel</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {product.profit_margin ? `${product.profit_margin}%` : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">Marge bénéficiaire</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center text-2xl font-bold text-yellow-500">
                      <Star className="w-6 h-6 mr-1" />
                      {product.rating || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">Note ({product.review_count || 0} avis)</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      #{product.best_seller_rank || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">BSR</div>
                  </div>
                </div>
              </Card>

              {/* Analyse de Concurrence */}
              <Card className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  <h4 className="font-medium text-gray-900">Analyse de Concurrence</h4>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Niveau de concurrence</span>
                    <Badge className={`${
                      product.competition_level === 'very_low' ? 'bg-green-100 text-green-800' :
                      product.competition_level === 'low' ? 'bg-yellow-100 text-yellow-800' :
                      product.competition_level === 'medium' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {product.competition_level === 'very_low' ? '🟢 Très faible' :
                       product.competition_level === 'low' ? '🟡 Faible' :
                       product.competition_level === 'medium' ? '🟠 Moyenne' :
                       '🔴 Élevée'}
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Nombre de vendeurs</span>
                    <span className="font-medium">{product.seller_count || 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Ventes estimées/mois</span>
                    <span className="font-medium">{product.estimated_sales || 'N/A'}</span>
                  </div>
                </div>
              </Card>

              {/* Analyse Financière */}
              <Card className="p-4">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="w-5 h-5 text-primary" />
                  <h4 className="font-medium text-gray-900">Analyse Financière Détaillée</h4>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {product.finance?.margin_percent ? `${(product.finance.margin_percent * 100).toFixed(1)}%` : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">Marge bénéficiaire</div>
                  </div>
                  
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {product.finance?.roi ? `${(product.finance.roi * 100).toFixed(1)}%` : 'N/A'}
                    </div>
                    <div className="text-sm text-gray-500">ROI</div>
                  </div>
                </div>

                {/* Détail des coûts */}
                <div className="space-y-3">
                  <h5 className="font-medium text-gray-900 border-b pb-2">Décomposition des coûts</h5>
                  
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Prix de vente</span>
                    <span className="font-medium">{product.finance?.offer_price ? `${product.finance.offer_price.toFixed(2)}€` : 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-red-600">
                    <span className="text-sm">Coût d'achat (COGS)</span>
                    <span className="font-medium">-{product.finance?.cogs_unit ? `${product.finance.cogs_unit.toFixed(2)}€` : 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-red-600">
                    <span className="text-sm">Frais de transport</span>
                    <span className="font-medium">-{product.finance?.freight_unit ? `${product.finance.freight_unit.toFixed(2)}€` : 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-red-600">
                    <span className="text-sm">Droits de douane</span>
                    <span className="font-medium">-{product.finance?.duty_amount ? `${product.finance.duty_amount.toFixed(2)}€` : 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-red-600">
                    <span className="text-sm">Frais Amazon (Referral)</span>
                    <span className="font-medium">-{product.finance?.referral_fee ? `${product.finance.referral_fee.toFixed(2)}€` : 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-red-600">
                    <span className="text-sm">Frais FBA</span>
                    <span className="font-medium">-{product.finance?.fba_fees ? `${product.finance.fba_fees.toFixed(2)}€` : 'N/A'}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-red-600">
                    <span className="text-sm">Stockage</span>
                    <span className="font-medium">-{product.finance?.storage_fee ? `${product.finance.storage_fee.toFixed(2)}€` : 'N/A'}</span>
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="flex justify-between items-center font-bold">
                      <span className="text-sm">Coût total (Landed Cost)</span>
                      <span className="text-red-600">{product.finance?.landed_cost_unit ? `${product.finance.landed_cost_unit.toFixed(2)}€` : 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-center font-bold text-green-600">
                      <span className="text-sm">Profit net unitaire</span>
                      <span>{product.finance?.margin_net ? `${product.finance.margin_net.toFixed(2)}€` : 'N/A'}</span>
                    </div>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-4 h-4 text-blue-600 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-blue-900">Point d'équilibre</div>
                        <div className="text-sm text-blue-700">
                          Prix de vente minimum: {product.finance?.breakeven_price ? `${product.finance.breakeven_price.toFixed(2)}€` : 'N/A'}
                        </div>
                        {product.finance?.meets_target_margin ? (
                          <div className="text-sm text-green-700 font-medium mt-1">✅ Atteint la marge cible</div>
                        ) : (
                          <div className="text-sm text-orange-700 font-medium mt-1">⚠️ En-dessous de la marge cible</div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Fournisseurs */}
              <Card className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Package className="w-5 h-5 text-primary" />
                    <h4 className="font-medium text-gray-900">Fournisseurs Vérifiés</h4>
                  </div>
                  <Badge variant="secondary">
                    {(product.suppliers && product.suppliers.length) || 0} trouvés
                  </Badge>
                </div>
                
                {/* Liste des fournisseurs */}
                {product.suppliers && product.suppliers.length > 0 ? (
                  <div className="space-y-3">
                    {product.suppliers.slice(0, 3).map((supplier, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="font-medium text-gray-900">{supplier.company_name || `Fournisseur ${index + 1}`}</div>
                            <div className="text-sm text-gray-500">{supplier.location || 'Chine'}</div>
                          </div>
                          <div className="flex gap-1">
                            {supplier.verified_flags?.includes('gold') && (
                              <Badge variant="secondary" className="text-xs">🥇 Gold</Badge>
                            )}
                            {supplier.verified_flags?.includes('verified') && (
                              <Badge variant="secondary" className="text-xs">✅ Vérifié</Badge>
                            )}
                            {supplier.verified_flags?.includes('trade_assurance') && (
                              <Badge variant="secondary" className="text-xs">🛡️ Trade</Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-gray-500">Prix:</span>
                            <div className="font-medium">{supplier.price_range || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">MOQ:</span>
                            <div className="font-medium">{supplier.min_order_quantity || 'N/A'}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Délai:</span>
                            <div className="font-medium">{supplier.lead_time || 'N/A'}</div>
                          </div>
                        </div>
                        
                        {supplier.certifications && supplier.certifications.length > 0 && (
                          <div className="mt-2">
                            <span className="text-xs text-gray-500">Certifications:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {supplier.certifications.slice(0, 3).map((cert, certIndex) => (
                                <Badge key={certIndex} variant="outline" className="text-xs">
                                  {cert}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500">
                    <Package className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <div className="text-sm">Aucun fournisseur trouvé</div>
                  </div>
                )}
                
                <Button
                  onClick={() => onViewSuppliers?.(product)}
                  className="w-full mt-3"
                  variant="outline"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Voir Tous les Fournisseurs
                </Button>
              </Card>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ProductDetailDrawer;